"use client";

import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { ASSET_PREFIX } from "@/config";

// 🔁 Convert your existing video popup gallery into an *image* popup gallery
export default function ImagePopupGallery() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // 🖼️ Image Data (kept from your snippet)
  const images = [
    {
      src: `${ASSET_PREFIX}/images/pluswilla-living-room.webp`,
      alt: "PlusWilla25 - living room in 4 BHK Villas",
    },
    {
      src: `${ASSET_PREFIX}/images/plus-villa-hall.webp`,
      alt: "PlusWilla 25 - Villa Hall in 3 BHK Villas",
    },
    {
      src: `${ASSET_PREFIX}/images/pluswilla-working-space.webp`,
      alt: "+Willa Working Space in 4 BHK Villas in Coimbatore", 
    },
    {
      src: `${ASSET_PREFIX}/images/plusvilla-bedroom.webp`,
      alt: "PlusVilla Bedroom in 3 BHK Villas for sale in Coimbatore",
    },
    {
      src: `${ASSET_PREFIX}/images/plusvilla-garden-space.webp`,
      alt: "+Willa25 Garden Space 3 BHK Villas", 
    },
    {
      src: `${ASSET_PREFIX}/images/plusvilla-balcony.webp`,
      alt: "Balcony area in PlusVilla 3 BHK villa Coimbatore",
    },
    {
      src: `${ASSET_PREFIX}/images/plusvilla-bedroom-2.webp`,
      alt: "3 BHK villa bedroom in PlusVilla 25 Coimbatore",
    },
    {
      src: `${ASSET_PREFIX}/images/plusvilla-dining-hall.webp`,
      alt: "Dining space in 4 BHK PlusVilla in Coimbatore",
    },
    {
      src: `${ASSET_PREFIX}/images/plus-willa-working-space.webp`,
      alt: "Working space in PlusVilla TVS Nagar Coimbatore",
    },
    {
      src: `${ASSET_PREFIX}/images/plus-villa-working-area.webp`,
      alt: "Working Area in PlusVilla luxury villas in Coimbatore",
    },
    {
      src: `${ASSET_PREFIX}/images/pluswilla-master-bedroom.webp`,
      alt: "PlusWilla Master Bedroom in 4 BHK Villas",
    },
    {
      src: `${ASSET_PREFIX}/images/plus-villa-modern-interior.webp`,
      alt: "Plus Willa Modern Interior in 3 BHK Villas", 
    },
    {
      src: `${ASSET_PREFIX}/images/dining-table-pluswilla.webp`,
      alt: "Dining table in PlusVilla luxury villas in Coimbatore",
    },
    {
      src: `${ASSET_PREFIX}/images/spacious-living-area-plus-willa.webp`,
      alt: "Spacious living area in PlusVilla luxury villas in Coimbatore",
    }, 
    {
      src: `${ASSET_PREFIX}/images/modern-gym-plusvilla.webp`,
      alt: "Modern Gym at Gated Community Villas",
    },
    {
      src: `${ASSET_PREFIX}/images/gym-equipments-plus-villa.webp`,
      alt: "Gym Equipments PlusWilla 25",
    },
    {
      src: `${ASSET_PREFIX}/images/indoor-games-plusWilla.webp`,
      alt: "Indoor Games PlusWilla",
    },
    {
      src: `${ASSET_PREFIX}/images/lounge-area-plus-willa.webp`,
      alt: "Lounge Area 3BHK Villas",
    },
    {
      src: `${ASSET_PREFIX}/images/plus-villa-meditation-area.webp`,
      alt: "Plus Willa Meditation Area in Gated Community Villas",
    },
    {
      src: `${ASSET_PREFIX}/images/plusvilla-living-area.webp`,
      alt: "PlusVilla Living Area 3BHK Villas in Coimbatore", 
    },
  ];

  // 📄 Pagination Config

  const [itemsPerPage, setItemsPerPage] = useState(9);

  useEffect(() => {
    const updateItemsPerPage = () => {
      if (window.innerWidth > 1000) {
        setItemsPerPage(9);
      } else if (window.innerWidth > 600) {
        setItemsPerPage(6);
      } else {
        setItemsPerPage(4);
      }
    };

    updateItemsPerPage(); // run on mount
    window.addEventListener("resize", updateItemsPerPage);
    return () => window.removeEventListener("resize", updateItemsPerPage);
  }, []);

  const totalPages = Math.ceil(images.length / itemsPerPage);
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = images.slice(indexOfFirst, indexOfLast);

  const handleNextPage = () =>
    setCurrentPage((p) => Math.min(p + 1, totalPages));
  const handlePrevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));

  const openAt = (absoluteIndex: number) => setActiveIndex(absoluteIndex);
  const close = () => setActiveIndex(null);

  const showPrev = useCallback(() => {
    setActiveIndex((idx) =>
      idx === null ? idx : (idx - 1 + images.length) % images.length
    );
  }, [images.length]);

  const showNext = useCallback(() => {
    setActiveIndex((idx) => (idx === null ? idx : (idx + 1) % images.length));
  }, [images.length]);

  // ⌨️ Keyboard navigation for modal
  useEffect(() => {
    if (activeIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", onKey);
    // Prevent body scroll while modal is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = originalOverflow;
    };
  }, [activeIndex, showPrev, showNext]);

  return (
    <section className="w-[85%] mx-auto">
      <h2 className="text-[28px]/[36px] md:text-[32px]/[40px] lg:text-[36px]/[44px] xl:text-[40px]/[48px] 2xl:text-[48px]/[54px] text-[#000] font-[500] mb-[30px] md:mb-[50px] text-center tracking-[-0.03em]">
        Our Gallery
      </h2>

      {/* Grid of Thumbnails */}
      <motion.div
        key={currentPage}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-9">
        {currentItems.map((item, i) => {
          const absoluteIndex = indexOfFirst + i;
          return (
            <button
              key={`${item.src}-${absoluteIndex}`}
              type="button"
              className="relative group overflow-hidden rounded-xl max-h-[310px] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black cursor-pointer"
              onClick={() => openAt(absoluteIndex)}
              aria-label={`Open image: ${item.alt}`}>
              <Image
                src={item.src}
                alt={item.alt}
                width={1200}
                height={800}
                className="w-full h-auto transition-transform duration-300 group-hover:scale-105 object-cover"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                priority={currentPage === 1 && i < 3}
              />

              {/* Subtle overlay on hover */}
              <div className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />

              {/* Zoom icon on hover */}
              <div className="pointer-events-none absolute inset-0 transition duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="inline-flex items-center justify-center transition duration-300  w-12 h-12 rounded-full bg-white/25 ">
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden>
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.3-4.3" />
                    <path d="M11 8v6M8 11h6" />
                  </svg>
                </span>
              </div>
            </button>
          );
        })}
      </motion.div>

      {/* Pagination Controls */}
      <div className="flex justify-center items-center mt-10 gap-4">
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          className={`px-5 py-2 rounded-full border border-gray-400 transition-all ${currentPage === 1
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-gray-100  cursor-pointer"
            }`}
          aria-label="Previous page">
          <svg
            xmlns="https://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 48 48">
            <path
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="4"
              d="M5.799 24h36m-24 12l-12-12l12-12"
            />
          </svg>
        </button>

        <span className="text-gray-600">
          Page {currentPage} of {totalPages}
        </span>

        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className={`px-5 py-2 rounded-full border border-gray-400 transition-all ${currentPage === totalPages
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-gray-100 cursor-pointer"
            }`}
          aria-label="Next page">
          <svg
            xmlns="https://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 48 48">
            <path
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="4"
              d="M42 24H6m24-12l12 12l-12 12"
            />
          </svg>
        </button>
      </div>

      {/* Popup Modal */}
      <AnimatePresence>
        {activeIndex !== null && (
          <motion.div
            key="popup"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4"
            onClick={close}
            aria-modal="true"
            role="dialog">
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-[1100px] rounded-xl overflow-hidden bg-black">
              {/* Full image */}
              <div className="relative w-full" style={{ aspectRatio: "16/9" }}>
                <Image
                  key={images[activeIndex].src}
                  src={images[activeIndex].src}
                  alt={images[activeIndex].alt}
                  fill
                  className="object-contain"
                  sizes="100vw"
                  priority
                />
              </div>

              {/* Caption */}
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[16px] leading-[22px] md:text-base p-3 md:p-4">
                {images[activeIndex].alt}
              </div>

              {/* Controls */}
              <button
                onClick={close}
                aria-label="Close"
                className="cursor-pointer absolute top-3 right-3 bg-white/20 hover:bg-white/40 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl"
                title="Close">
                ✕
              </button>

              <button
                onClick={showPrev}
                aria-label="Previous image"
                className="cursor-pointer absolute left-3 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full w-10 h-10 flex items-center justify-center"
                title="Previous">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>

              <button
                onClick={showNext}
                aria-label="Next image"
                className="cursor-pointer absolute right-3 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full w-10 h-10 flex items-center justify-center"
                title="Next">
                <svg
                  xmlns="https://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
